import { ColumnDef } from '@tanstack/react-table';
import { EllipsisIcon } from 'lucide-react';
import type { BlockedProductListEntry } from 'contracts/blocked-products';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { DataTableHelper, Formatter } from 'utils';

type Props = {
  onUpdateReason?: (selectedProduct: BlockedProductListEntry) => void;
  showSelectionColumn: boolean;
  showActionsDropdown: boolean;
}

const dt = new DataTableHelper<BlockedProductListEntry>();

export const useBlockedProductColumns = ({
                                           onUpdateReason,
                                           showSelectionColumn,
                                           showActionsDropdown,
                                         }: Props): ColumnDef<BlockedProductListEntry>[] => {
  const selection = dt.buildSelectionByRowColumn();
  const plant = dt.buildColumn({
    id: 'Planta',
    size: 120,
    accessorKey: 'plant',
    content: (row) => row.plant,
  });
  const destinationPlant = dt.buildColumn({
    id: 'Planta Destino',
    size: 120,
    accessorKey: 'destinationPlant',
    content: (row) => row.destinationPlant ?? '---',
  });
  const storageLocation = dt.buildColumn({
    id: 'SLOC',
    size: 120,
    accessorKey: 'storageLocation',
    content: (row) => row.storageLocation,
  });
  const material = dt.buildColumn({
    id: 'Código',
    size: 120,
    accessorKey: 'material',
    content: (row) => row.material,
  });
  const materialDescription = dt.buildColumn({
    id: 'Produto',
    size: 400,
    accessorKey: 'materialDescription',
    content: (row) => (
      <div
        className={'w-[210px] truncate'}
        title={row.materialDescription}
      >
        {row.materialDescription}
      </div>
    ),
  });
  const batch = dt.buildColumn({
    id: 'Lote',
    size: 120,
    accessorKey: 'batch',
    content: (row) => row.batch,
  });
  const expirationDate = dt.buildColumn({
    id: 'Dt. Venc.',
    size: 120,
    accessorKey: 'shelfLifeExpirationDate',
    content: (row) => row.shelfLifeExpirationDate
      ? Formatter.date(row.shelfLifeExpirationDate)
      : '---',
  });
  const createdAt = dt.buildColumn({
    id: 'Dt. Bloq.',
    size: 120,
    accessorKey: 'createdAt',
    content: (row) => Formatter.date(row.createdAt),
  });
  const amount = dt.buildColumn({
    id: 'Quantidate',
    size: 120,
    accessorKey: 'blockedStock',
    content: (row) => row.blockedStock || '---',
  });
  const usdValue = dt.buildColumn({
    id: 'Valor USD',
    size: 120,
    accessorKey: 'totalValue',
    content: (row) => row.totalValue
      ? Formatter.currency(row.totalValue, 'USD')
      : '---',
  });
  const estimation = dt.buildColumn<string>({
    id: 'Estimativa',
    size: 120,
    accessorKey: 'estimation',
    // TODO: calculate this on the API in a future implementation
    content: () => '---',
  });
  const reason = dt.buildColumn({
    id: 'Motivo',
    size: 400,
    accessorFn: (row) => row.productReasonType?.id,
    content: (row) => (
      <div
        className={'w-[210px] truncate'}
        title={row.productReasonType?.description}
      >
        {row.productReasonType?.description ?? '---'}
      </div>
    ),
  });
  const actions = dt.buildColumn<string>({
    id: 'Actions',
    size: 48,
    title: '',
    accessorKey: 'actions',
    content: (row) => {
      return (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger onClick={(event) => {
            event.stopPropagation();
          }}>
            <EllipsisIcon className={'text-gray-600'} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align={'end'}>
            <DropdownMenuItem onClick={(event) => {
              onUpdateReason?.(row);
              event.stopPropagation();
            }}>
              Alterar motivo
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      );
    },
    enableHiding: false,
    enableSorting: false,
  });

  return [
    ...(showSelectionColumn && [selection] || []),
    plant,
    destinationPlant,
    storageLocation,
    material,
    materialDescription,
    batch,
    expirationDate,
    createdAt,
    amount,
    usdValue,
    estimation,
    reason,
    ...(showActionsDropdown && [actions] || []),
  ];
};